import { cn } from "@/common/utils/css.utils";
import { cva, VariantProps } from "class-variance-authority";
import { forwardRef, Ref, ComponentProps } from "react";

const CheckBoxStyle = cva(
  cn(
    "grid h-5 w-5 shrink-0 cursor-pointer appearance-none place-content-center rounded-[4px] border-2 border-grey-200 focus:outline-none focus:ring-2 focus:ring-curious-200",
    "before:h-3 before:w-2 before:border-b-2 before:border-r-2 before:border-white",
    "before:-translate-y-0.5 before:rotate-45 before:scale-0 before:transition-transform",
    "checked:before:scale-100",
    "disabled:cursor-not-allowed disabled:border-grey-300 disabled:bg-grey-100 disabled:checked:bg-grey-300 disabled:checked:before:border-grey-600"
  ),
  {
    variants: {
      color: {
        brand: "checked:border-brand-500 checked:bg-brand-500",
        curious: "checked:border-curious-500 checked:bg-curious-500",
        attention: "checked:border-attention-500 checked:bg-attention-500",
      },
      disabled: {
        true: "",
        false: "",
      },
    },
    compoundVariants: [
      {
        color: "brand",
        disabled: false,
        className: "md:hover:border-brand-300 md:checked:hover:bg-brand-300",
      },
      {
        color: "curious",
        disabled: false,
        className:
          "md:hover:border-curious-300 md:checked:hover:bg-curious-300",
      },
      {
        color: "attention",
        disabled: false,
        className:
          "md:hover:border-attention-300 md:checked:hover:bg-attention-300",
      },
    ],
    defaultVariants: {
      color: "brand",
    },
  }
);

type CheckBoxBaseProps = {
  ref?: Ref<HTMLInputElement>;
  elementRef?: Ref<HTMLInputElement>;
} & Omit<ComponentProps<"input">, "ref" | "color"> &
  VariantProps<typeof CheckBoxStyle>;

const CheckBoxBase = ({
  elementRef,
  color,
  disabled,
  className,
  ...props
}: CheckBoxBaseProps) => {
  return (
    <input
      {...props}
      type="checkbox"
      ref={elementRef}
      disabled={disabled}
      className={cn(CheckBoxStyle({ color, disabled }), className)}
    />
  );
};

CheckBoxBase.displayName = "CheckBox";

export type CheckBoxProps = Omit<CheckBoxBaseProps, "elementRef">;

/**
 * Simple check box.
 */
export const CheckBox = forwardRef<HTMLInputElement, CheckBoxProps>(
  (props, ref) => <CheckBoxBase {...props} elementRef={ref} />
);
